import { useCallback, useEffect, useRef, useState } from 'react';

export type LockedTooltip<Point> = {
  point: Point;
  position: { x: number; y: number };
};

// Keeps the tooltip open long enough to move the pointer from the dot onto its buttons.
export const TOOLTIP_HIDE_DELAY_MS = 600;

const useTooltipLock = <Point>({
  interactive,
  hasButtons,
}: {
  interactive: boolean;
  hasButtons: (point: Point) => boolean;
}) => {
  const [lockedTooltip, setLockedTooltip] =
    useState<LockedTooltip<Point> | null>(null);
  const [dotHovered, setDotHovered] = useState(false);
  const lastShownTooltipRef = useRef<LockedTooltip<Point> | null>(null);
  const tooltipHoveredRef = useRef(false);
  const hideTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => () => clearTimeout(hideTooltipTimeoutRef.current), []);

  const onDotHovered = useCallback(
    (hovered: boolean) => {
      clearTimeout(hideTooltipTimeoutRef.current);
      setDotHovered(hovered);
      if (hovered) {
        if (!tooltipHoveredRef.current) setLockedTooltip(null);
        return;
      }
      const shown = lastShownTooltipRef.current;
      if (!shown || !interactive || !hasButtons(shown.point)) return;
      setLockedTooltip(shown);
      hideTooltipTimeoutRef.current = setTimeout(() => {
        if (!tooltipHoveredRef.current) setLockedTooltip(null);
      }, TOOLTIP_HIDE_DELAY_MS);
    },
    [interactive, hasButtons],
  );

  const onTooltipShown = useCallback((shown: LockedTooltip<Point>) => {
    lastShownTooltipRef.current = shown;
  }, []);

  const onTooltipHoverChange = useCallback((hovered: boolean) => {
    tooltipHoveredRef.current = hovered;
  }, []);

  return {
    lockedTooltip,
    dotHovered,
    onDotHovered,
    onTooltipShown,
    onTooltipHoverChange,
    onTooltipLock: setLockedTooltip,
  };
};

export default useTooltipLock;
